import { ApolloError } from '@apollo/client';
import { Alert, Button, InputField } from '@design-system';
import classNames from 'classnames';
import { Trans, useTranslation } from 'next-i18next';
import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  LazyCloudflareTurnstile,
  useResetTurnstile,
} from '../../../../components/Captcha/LazyCloudflareTurnstile';
import UnderlinedLink from '../../../../components/UnderlinedLink';
import { generalSubscriptionsInput } from '../../../../constants/subscriptions';
import { pathFor } from '../../../../helpers/links';
import { useAuth } from '../../../../providers/AuthProvider';
import { AuthState } from '../../../../types/Auth';
import { AnalyticsEmitter } from '../../../../utils/analytics/emitter';
import { AnalyticsObjectProvider } from '../../../../utils/analytics/object';
import { captureError } from '../../../../utils/capture';
import { extractApolloError } from '../../../../utils/errors';
import { ApiError } from '../../../auth/components/ApiError';
import { useGsiSetup } from '../../../auth/hooks/useGsiSetup';
import { useSubscriptionsManager } from '../../../auth/hooks/useSubscriptionsManager';
import { inAppBrowser } from '../../../auth/utils/inAppBrowser';

const RESEND_COOLDOWN_SECONDS = 30;

type EmailFormValues = { email: string };
type CodeFormValues = { code: string };

type EmailScreenProps = {
  emitter: AnalyticsEmitter;
  onCodeSent: () => void;
};

const EmailScreen = ({ emitter, onCodeSent }: EmailScreenProps) => {
  const { t } = useTranslation(['authentication', 'checkout']);
  const { authState, loginGoogle, signupAndSubscribe } = useAuth();
  const { subscribe } = useSubscriptionsManager();
  const [captchaToken, setCaptchaToken] = useState<string>();
  const { turnstileRef, turnstileReset } = useResetTurnstile();
  const [apiError, setApiError] = useState<ApolloError | null>(null);
  const [googleError, setGoogleError] = useState(false);

  const form = useForm<EmailFormValues>({ defaultValues: { email: '' } });
  const emailValidation = form.formState.errors.email?.message;

  const showGoogleAuth = !inAppBrowser();

  const { gsiContainer, handleGsi } = useGsiSetup(async (response) => {
    setGoogleError(false);
    try {
      await loginGoogle(response.credential);
      emitter('google', 'success');
      await subscribe([generalSubscriptionsInput]);
    } catch (e) {
      captureError(e as Error);
      setGoogleError(true);
      emitter('google', 'error', { error: (e as Error).message });
    }
  });

  const handleSubmit = useCallback(
    (ev: FormEvent<HTMLFormElement>) => {
      form.handleSubmit(async (values: EmailFormValues) => {
        ev.preventDefault();
        emitter('email', 'submit');
        setApiError(null);

        const { data, errors } = await signupAndSubscribe({
          captchaToken,
          email: values.email.trim(),
          subscriptions: [generalSubscriptionsInput],
        });

        const error = extractApolloError(errors);
        if (error) {
          setApiError(error);
          turnstileReset();
          emitter('email', 'error', { error });
          return;
        }

        if (data?.signupAndSubscribe === true) {
          emitter('email', 'success');
          onCodeSent();
        }
      })(ev);
    },
    [
      captchaToken,
      emitter,
      form,
      onCodeSent,
      signupAndSubscribe,
      turnstileReset,
    ],
  );

  return (
    <form className="flex w-full flex-col gap-6" onSubmit={handleSubmit}>
      {(apiError || googleError) && (
        <Alert status="error">{t('authentication:errors.generic')}</Alert>
      )}

      <InputField
        {...form.register('email', {
          required: {
            value: true,
            message: t(
              'authentication:screens.email.input.validation.required',
            ),
          },
          pattern: {
            value: /\S+@\S+\.\S+/,
            message: t('authentication:screens.email.input.validation.invalid'),
          },
        })}
        dataTestid="checkout-auth-email"
        error={emailValidation}
        id="checkout-auth-email"
        label={t('checkout:details.auth.emailLabel')}
        name="email"
        type="email"
      />

      <div className="flex w-full flex-col">
        <LazyCloudflareTurnstile
          actionName="sign_in_email"
          onError={() => setCaptchaToken(void 0)}
          onExpire={() => setCaptchaToken(void 0)}
          onLoadStart={() => setCaptchaToken(void 0)}
          onSuccess={setCaptchaToken}
          turnstileRef={turnstileRef}
        />

        <Button
          disabled={captchaToken === void 0}
          fullWidth
          label={t('checkout:details.auth.continue')}
          loading={authState === AuthState.LOADING}
          type="submit"
          variant="primary"
        />
      </div>

      {showGoogleAuth && (
        <>
          <span className="mx-auto text-sm text-neutral-4">
            {t('checkout:details.auth.or')}
          </span>

          <div className="relative">
            <div ref={gsiContainer} style={{ display: 'none' }} />

            <Button
              fullWidth
              label={t('authentication:google')}
              onClick={() => {
                handleGsi();
                emitter('google', 'clicked');
              }}
              type="button"
              variant="tertiary"
            />
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              alt="Google sign in"
              className="pointer-events-none absolute left-6 top-1/2 h-5 w-5 -translate-y-1/2 select-none"
              src="/images/google-signin-logo.svg"
            />
          </div>
        </>
      )}

      <p className="px-5 text-center text-neutral-4">
        <Trans i18nKey="screens.signUp.captionOptInGeneral" ns="authentication">
          By continuing, you’re agreeing to our&nbsp;
          <UnderlinedLink
            href={pathFor({ page: 'TERMS_AND_CONDITIONS' })}
            target="_blank"
          >
            terms &amp; conditions
          </UnderlinedLink>
          {' and '}
          <UnderlinedLink
            href={pathFor({ page: 'PRIVACY_POLICY' })}
            target="_blank"
          >
            privacy policy
          </UnderlinedLink>
          &nbsp;and opting in to receive updates and marketing emails.
        </Trans>
      </p>
    </form>
  );
};

type CodeScreenProps = {
  emitter: AnalyticsEmitter;
};

const CodeScreen = ({ emitter }: CodeScreenProps) => {
  const { t } = useTranslation(['authentication', 'checkout']);
  const { authState, emailForCode, signupAndSubscribe, validateCode } =
    useAuth();
  const [captchaToken, setCaptchaToken] = useState<string>();
  const { turnstileRef, turnstileReset } = useResetTurnstile();
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [apiError, setApiError] = useState<ApolloError | null>(null);

  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const form = useForm<CodeFormValues>({ defaultValues: { code: '' } });
  const validationError = form.formState.errors.code?.message;

  const requestNewCode = useCallback(async () => {
    emitter('resend', 'clicked');
    if (!emailForCode) return;

    setShowCaptcha(true);
    try {
      const response = await signupAndSubscribe({
        captchaToken,
        email: emailForCode,
        subscriptions: [],
      });

      const error = extractApolloError(response?.errors);
      if (error) emitter('resend', 'error', { error });

      setApiError(error);
    } catch (e) {
      emitter('resend', 'error', { error: (e as Error).message });
      setApiError(e as ApolloError);
    } finally {
      setShowCaptcha(false);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    }
  }, [captchaToken, emailForCode, emitter, signupAndSubscribe]);

  const handleSubmit = useCallback(
    (ev: FormEvent<HTMLFormElement>) => {
      form.handleSubmit(async (values: CodeFormValues) => {
        ev.preventDefault();
        if (!emailForCode) return;

        emitter('code', 'submit');
        setApiError(null);

        try {
          const { data, errors } = await validateCode(
            values.code,
            emailForCode,
          );

          const error = extractApolloError(errors);
          if (error) {
            setApiError(error);
            emitter('code', 'error', { error });
            return;
          }

          const { account, isNewAccount } = data?.useLoginCode || {};
          if (account?.id) {
            emitter('code', 'success', {
              account_already_exists: !isNewAccount,
            });
          }
        } catch (e) {
          turnstileReset();
          setApiError(e as ApolloError);
          emitter('code', 'error', { error: (e as Error).message });
        }
      })(ev);
    },
    [emailForCode, emitter, form, turnstileReset, validateCode],
  );

  return (
    <form className="flex w-full flex-col gap-4" onSubmit={handleSubmit}>
      {apiError ? (
        <ApiError apiError={apiError} retry={requestNewCode} />
      ) : (
        <Alert status="success">
          {t('authentication:screens.verifyCode.explainerBanner')}
        </Alert>
      )}

      <InputField
        disabled
        id="checkout-auth-email-readonly"
        label={t('authentication:screens.verifyCode.emailInputLabel')}
        name="email"
        value={emailForCode}
      />

      <div className="relative">
        <InputField
          {...form.register('code', {
            required: {
              value: true,
              message: t(
                'authentication:screens.verifyCode.validation.required',
              ),
            },
          })}
          dataTestid="checkout-auth-code"
          error={validationError}
          id="checkout-auth-code"
          label={t('authentication:screens.verifyCode.label')}
          name="code"
        />
        <button
          className={classNames('hyperlink absolute right-4', {
            'top-[40%]': validationError,
            'top-1/2': !validationError,
            'cursor-not-allowed text-neutral-4 no-underline':
              resendCooldown > 0,
          })}
          disabled={resendCooldown > 0}
          onClick={() => void requestNewCode()}
          type="button"
        >
          {resendCooldown > 0
            ? t('authentication:screens.verifyCode.resendBtnCooldown', {
                seconds: resendCooldown,
              })
            : t('authentication:screens.verifyCode.resendBtn')}
        </button>
      </div>

      <LazyCloudflareTurnstile
        actionName="sign_in_email"
        className={classNames({ 'hidden': !showCaptcha })}
        onError={() => setCaptchaToken(void 0)}
        onExpire={() => setCaptchaToken(void 0)}
        onLoadStart={() => setCaptchaToken(void 0)}
        onSuccess={setCaptchaToken}
        turnstileRef={turnstileRef}
      />

      <Button
        fullWidth
        label={t('authentication:screens.verifyCode.continueBtn')}
        loading={authState === AuthState.LOADING}
        type="submit"
        variant="primary"
      />
    </form>
  );
};

export type DetailsAuthGateProps = {
  helpText: string;
};

export const DetailsAuthGate = ({ helpText }: DetailsAuthGateProps) => {
  const { authState, emailForCode } = useAuth();
  const [codeSent, setCodeSent] = useState(
    authState === AuthState.CODE_PENDING && Boolean(emailForCode),
  );

  return (
    <AnalyticsObjectProvider name="checkout auth" sendViewedEvent={false}>
      {(emitter) => (
        <div className="flex flex-col gap-9 lg:gap-12">
          <p className="text-center">{helpText}</p>

          {codeSent && emailForCode ? (
            <CodeScreen emitter={emitter} />
          ) : (
            <EmailScreen
              emitter={emitter}
              onCodeSent={() => setCodeSent(true)}
            />
          )}
        </div>
      )}
    </AnalyticsObjectProvider>
  );
};
